import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useUser, useClerk } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { RefreshCw, Calendar, Search, Filter } from "lucide-react";

interface Reservation {
  id: string;
  date: string;
  time: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  partySize: number;
  status: "PENDING" | "CONFIRMED" | "CANCELED" | "COMPLETED" | "NO_SHOW";
  specialRequests?: string;
}

export default function Reservations() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { user, isSignedIn, isLoaded } = useUser();
  const { session } = useClerk();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  // Function to get auth headers
  const getAuthHeaders = async () => {
    if (!session) {
      console.error("No active session");
      return {};
    }

    const token = await session.getToken();
    return {
      Authorization: `Bearer ${token}`,
    };
  };

  const {
    data: reservations,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["reservations"],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await axios.get("/api/reservations", { headers });
      return response.data as Reservation[];
    },
    enabled: !!isSignedIn && !!session,
  });

  // Filter reservations based on search term and status filter
  const filteredReservations = reservations?.filter((reservation) => {
    const matchesSearch = searchTerm
      ? reservation.customerName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        reservation.customerEmail
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      : true;

    const matchesStatus = statusFilter
      ? reservation.status === statusFilter
      : true;

    return matchesSearch && matchesStatus;
  });

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>{t("common.loading")}</p>
      </div>
    );
  }

  if (!isSignedIn) {
    navigate("/sign-in");
    return null;
  }

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "CONFIRMED":
        return "bg-green-100 text-green-800";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "CANCELED":
        return "bg-red-100 text-red-800";
      case "COMPLETED":
        return "bg-blue-100 text-blue-800";
      case "NO_SHOW":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">{t("reservations.title")}</h1>
        <Button onClick={() => refetch()} className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          {t("common.refresh")}
        </Button>
      </div>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>{t("reservations.filters")}</CardTitle>
          <CardDescription>
            {t("reservations.filtersDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative w-full md:w-2/3">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("reservations.searchPlaceholder")}
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="w-full md:w-1/3 flex gap-2">
              <Button
                variant={statusFilter === null ? "default" : "outline"}
                onClick={() => setStatusFilter(null)}
                className="flex-1"
              >
                {t("reservations.all")}
              </Button>
              <Button
                variant={statusFilter === "PENDING" ? "default" : "outline"}
                onClick={() => setStatusFilter("PENDING")}
                className="flex-1"
              >
                {t("reservations.pending")}
              </Button>
              <Button
                variant={statusFilter === "CONFIRMED" ? "default" : "outline"}
                onClick={() => setStatusFilter("CONFIRMED")}
                className="flex-1"
              >
                {t("reservations.confirmed")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("reservations.upcoming")}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">
              <p>{t("common.loading")}</p>
            </div>
          ) : filteredReservations?.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("reservations.date")}</TableHead>
                  <TableHead>{t("reservations.time")}</TableHead>
                  <TableHead>{t("reservations.customer")}</TableHead>
                  <TableHead>{t("reservations.partySize")}</TableHead>
                  <TableHead>{t("reservations.status")}</TableHead>
                  <TableHead className="text-right">
                    {t("common.actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReservations.map((reservation) => (
                  <TableRow key={reservation.id}>
                    <TableCell>
                      {new Date(reservation.date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{reservation.time}</TableCell>
                    <TableCell>
                      <div>
                        <div>{reservation.customerName}</div>
                        <div className="text-sm text-muted-foreground">
                          {reservation.customerEmail}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{reservation.partySize}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(
                          reservation.status
                        )}`}
                      >
                        {t(
                          `reservations.status.${reservation.status.toLowerCase()}`
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          navigate(`/reservations/${reservation.id}`)
                        }
                      >
                        {t("common.view")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <Calendar className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">
                {t("reservations.noReservations")}
              </h3>
              <p className="mt-2 text-muted-foreground">
                {t("reservations.noReservationsDescription")}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
